import { QuestionnaireAnswer, QuestionnaireQuestionOption } from "./types";

export interface QuestionnaireAnswerContext {
  orderedOptions: QuestionnaireQuestionOption[];
  selectedValues: string[];
  selectedTexts: string[];
  currentOtherText: string;
}

const getOtherOption = (orderedOptions: QuestionnaireQuestionOption[]) => {
  return orderedOptions.find((option) => option.is_other);
};

export const isOtherOptionVisuallySelected = (
  option: QuestionnaireQuestionOption,
  selectedValues: string[],
  isOtherDictating: boolean
) => {
  if (!option.is_other) {
    return selectedValues.includes(option.value);
  }

  return selectedValues.includes(option.value) || isOtherDictating;
};

export const ensureOtherOptionSelected = (
  context: QuestionnaireAnswerContext
): QuestionnaireAnswer | null => {
  const otherOption = getOtherOption(context.orderedOptions);
  if (!otherOption || context.selectedValues.includes(otherOption.value)) {
    return null;
  }

  const normalizedOtherText = context.currentOtherText.trim();
  const nextTexts = context.selectedTexts.filter((item) => item !== otherOption.label);

  nextTexts.push(normalizedOtherText.length > 0 ? normalizedOtherText : otherOption.label);

  return {
    value: [...context.selectedValues, otherOption.value],
    text: nextTexts,
    otherText: context.currentOtherText || null,
  };
};

export const toggleMultipleChoiceOption = (
  option: QuestionnaireQuestionOption,
  context: QuestionnaireAnswerContext,
  isOtherDictating: boolean
): QuestionnaireAnswer | null => {
  if (isOtherDictating && option.is_other) {
    return null;
  }

  const exists = context.selectedValues.includes(option.value);

  if (exists) {
    const filteredValues = context.selectedValues.filter((item) => item !== option.value);
    const filteredTexts = context.selectedTexts.filter(
      (item) => item !== option.label && item !== context.currentOtherText
    );

    return {
      value: filteredValues,
      text: filteredTexts,
      otherText: option.is_other ? null : context.currentOtherText || null,
    };
  }

  return {
    value: [...context.selectedValues, option.value],
    text: [...context.selectedTexts, option.label],
    otherText: context.currentOtherText || null,
  };
};

export const updateOtherTextAnswer = (
  value: string,
  context: QuestionnaireAnswerContext
): QuestionnaireAnswer => {
  const trimmed = value.trim();
  const otherOption = getOtherOption(context.orderedOptions);

  if (!otherOption) {
    return {
      value: context.selectedValues,
      text: context.selectedTexts,
      otherText: trimmed || null,
    };
  }

  const hasOtherSelected = context.selectedValues.includes(otherOption.value);

  if (!hasOtherSelected) {
    const nextValues = [...context.selectedValues, otherOption.value];
    const nextTexts = context.selectedTexts.filter((item) => item !== otherOption.label);

    nextTexts.push(trimmed.length > 0 ? trimmed : otherOption.label);

    return {
      value: nextValues,
      text: nextTexts,
      otherText: value || null,
    };
  }

  const nextTexts = context.selectedTexts.filter(
    (item) => item !== otherOption.label && item !== context.currentOtherText.trim()
  );

  nextTexts.push(trimmed.length > 0 ? trimmed : otherOption.label);

  return {
    value: context.selectedValues,
    text: nextTexts,
    otherText: value || null,
  };
};
